import React, {useEffect, useState} from 'react'
import {useDispatch} from 'react-redux'
import {useSearchHotelsQuery} from 'services/partnersApi'
import {getPapiLabel, WidgetLabelTypes} from 'utils/label'
import {getUrlParams} from 'utils/urlParams'
import {getSearchQueryFromProps} from 'widgets/helpers/getSearchQueryFromProps'
import {getHotelsToShow} from 'widgets/helpers/hotel'
import {buildWidgetConfig} from 'widgets/helpers/widgetConfig'

import {trackEvent} from '@daedalus/core/src/analytics/modules/actions'
import {
  Action,
  Category,
  Entity
} from '@daedalus/core/src/analytics/types/Events'
import {brands} from '@daedalus/core/src/brand/config'
import {availableCountries} from '@daedalus/core/src/localization/business/countries'
import {getTextDirection} from '@daedalus/core/src/localization/services/locale'

import {SearchQuery} from '../../types/partnersApi/types'
import MultipleHotelsList from './MultipleHotelsList/MultipleHotelsList'

export type WidgetProps = Awaited<ReturnType<typeof buildWidgetConfig>>

export const MultipleHotelsWidget = ({
  partnerKey,
  hotelIds,
  hotelNames,
  city,
  country,
  checkIn,
  checkOut,
  rooms,
  language,
  currency,
  anonymousId,
  userDevice,
  placeName = '',
  placeId,
  userCountry,
  forcedUserCountry,
  label,
  distanceUnit
}: WidgetProps) => {
  const [searchCheckIn, setSearchCheckIn] = useState(checkIn)
  const [searchCheckOut, setSearchCheckOut] = useState(checkOut)
  const [searchDestination, setSearchDestination] = useState(placeName)

  const dispatch = useDispatch()

  if (!placeName && !placeId && !hotelIds.length && !hotelNames.length) {
    throw new Error(
      'Please provide one of the parameters: place name, place id, hotel ids or hotel names'
    )
  }
  const {isRtl} = getTextDirection(language)

  const {hideDividers, title} = getUrlParams()
  const isHideDividers = hideDividers === 'true'
  const titleValue = typeof title === 'string' ? title : ''

  const query: SearchQuery = getSearchQueryFromProps({
    placeId,
    placeName,
    hotelIds,
    hotelNames,
    country,
    city
  })

  const isSearchByPlace = Boolean(query.place || query.placeId)

  const {data, error, isFetching, isLoading, refetch} = useSearchHotelsQuery(
    {
      partnerKey,
      payload: {
        query,
        attributes: ['availableHotelsCount'],
        hotelAttributes: ['details'],
        language,
        currency,
        anonymousId,
        userDevice,
        userCountry: forcedUserCountry,
        checkIn,
        checkOut,
        rooms,
        label: getPapiLabel(WidgetLabelTypes.multipleHotels, label)
      },
      widgetType: WidgetLabelTypes.multipleHotels
    },
    {
      refetchOnMountOrArgChange: true
    }
  )

  const taxDisplayLogic = availableCountries[userCountry]
  const isDataLoaded = !isFetching && !!data

  const hotelsToShow = getHotelsToShow({hotels: data?.hotels})

  const isSoldOut = hotelsToShow.length === 0
  const firstHotelUrl = hotelsToShow.length
    ? hotelsToShow[0].details?.vioUrl
    : undefined
  const placeUrl = data?.place?.vioUrl
  const ctaButtonUrl = isSearchByPlace && placeUrl ? placeUrl : firstHotelUrl

  const placeDisplayName = data?.place?.name
  const availableHotelsCount = data?.availableHotelsCount

  const onSupportButtonClick = () => {
    window.open(brands.vio.contactUrl, '_blank')
  }

  const onReloadButtonClick = () => {
    refetch()
  }
  // We can request offers without check-in and check-out dates or with placeId instead of placeName.
  // But we want to sync our interface with dates and place name received from SAPI.
  // So this useEffect is needed to not trigger additional SAPI request when we set up dates and place name from first SAPI response.
  useEffect(() => {
    if (
      data?.stay?.checkIn ||
      data?.stay?.checkOut ||
      data?.place?.name ||
      data?.place?.id
    ) {
      setSearchCheckIn(data?.stay?.checkIn)
      setSearchCheckOut(data?.stay?.checkOut)
      setSearchDestination(data?.place?.name || '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data?.stay?.checkIn,
    data?.stay?.checkOut,
    data?.place?.name,
    data?.place?.id
  ])

  useEffect(() => {
    dispatch(
      trackEvent({
        category: Category.System,
        entity: Entity.Widget,
        action: Action.Displayed,
        payload: {
          partnerKey,
          widgetType: WidgetLabelTypes.multipleHotels,
          label
        }
      })
    )
  }, [])

  return (
    <MultipleHotelsList
      searchCheckIn={searchCheckIn}
      searchCheckOut={searchCheckOut}
      hotelsToShow={hotelsToShow}
      isDataLoaded={isDataLoaded}
      isFetching={isFetching}
      isLoading={isLoading}
      isHideDividers={isHideDividers}
      isRtl={isRtl}
      isSoldOut={isSoldOut}
      onReloadClick={onReloadButtonClick}
      onSupportButtonClick={onSupportButtonClick}
      taxDisplayLogic={taxDisplayLogic}
      ctaButtonUrl={ctaButtonUrl}
      currency={currency}
      placeName={placeDisplayName}
      availableHotelsCount={availableHotelsCount}
      error={error}
      rooms={rooms}
      title={titleValue}
      placeId={placeId}
      partnerKey={partnerKey}
      label={label}
      distanceUnit={distanceUnit}
      searchDestination={searchDestination}
    />
  )
}
